<template>
  <div class="chat-message" :class="`chat-message--${role}`">
    <div v-if="role === 'assistant'" class="chat-message__avatar" aria-hidden="true" />

    <div class="chat-message__bubble" :class="`chat-message__bubble--${role}`">
      <template v-if="renderItems.length">
        <template v-for="(item, index) in renderItems" :key="item.key ?? index">
          <!-- Single, non-bundled block -->
          <template v-if="item.kind === 'block'">
            <div
              v-if="item.block.kind === 'text'"
              class="chat-message__text"
              v-html="renderMarkdown(item.block.text)"
            />

            <div
              v-else-if="item.block.kind === 'thinking'"
              class="chat-message__thinking"
              :class="{ 'chat-message__thinking--live': isLiveThinking(item.block) }"
            >
              <q-icon name="more_horiz" size="12px" class="chat-message__thinking-icon" />
              <span>{{ item.block.text }}</span>
              <span
                v-if="isLiveThinking(item.block) && elapsedSeconds(item.block) >= 3"
                class="chat-message__thinking-meta"
              >
                · {{ formatElapsed(item.block) }}{{ stageHint(item.block) }}
              </span>
            </div>

            <MessagePermissionDenied
              v-else-if="item.block.kind === 'permission-denied'"
              :reason="item.block.reason"
              :alternative="item.block.alternative"
            />

            <MessagePreviewStatus
              v-else-if="item.block.kind === 'preview'"
              :preview-run-id="item.block.previewRunId"
              :status="item.block.status"
              :error-message="item.block.errorMessage"
              :result-inline-json="item.block.resultInlineJson"
              :proposal-summary="item.block.proposalSummary"
            />

            <MessageProposalCard
              v-else-if="item.block.kind === 'proposal'"
              :proposal="item.block.proposal"
              :busy="item.block.busy"
              :previewing="item.block.previewing"
              :committed="item.block.committed"
              :committed-rule-id="item.block.committedRuleId"
              :dismissed="item.block.dismissed"
              :dismissing="item.block.dismissing"
              :error="item.block.error"
              @confirm="$emit('confirm-proposal', item.block.proposal.proposalId)"
              @preview="$emit('preview-proposal', item.block.proposal.proposalId)"
              @dismiss="$emit('dismiss-proposal', item.block.proposal.proposalId)"
            />
          </template>

          <!-- A bundle: 1+ proposal cards emitted from one user message -->
          <section
            v-else-if="item.kind === 'bundle'"
            class="chat-message__bundle"
            :aria-label="`Proposed together (${item.proposals.length})`"
          >
            <header class="chat-message__bundle-head">
              <span class="chat-message__bundle-title">
                <q-icon name="auto_awesome_motion" size="13px" />
                Proposed together
              </span>
              <!-- Bundle-level actions only when 2+ siblings are still pending. Once the
                   bundle has degenerated to a single actionable card, the per-card buttons
                   are the only sensible affordances. -->
              <div v-if="actionablePendingCount(item) >= 2" class="chat-message__bundle-actions">
                <button
                  type="button"
                  class="chat-message__bundle-btn"
                  @click="dismissBundle(item)"
                >
                  Dismiss all
                </button>
                <button
                  type="button"
                  class="chat-message__bundle-btn"
                  :disabled="bundlePreviewBusy(item)"
                  @click="previewBundle(item)"
                >
                  Preview all
                </button>
                <button
                  type="button"
                  class="chat-message__bundle-btn chat-message__bundle-btn--primary"
                  @click="confirmBundle(item)"
                >
                  Apply all
                </button>
              </div>
            </header>

            <MessageProposalCard
              v-for="proposalBlock in item.proposals"
              :key="proposalBlock.id"
              :proposal="proposalBlock.proposal"
              :busy="proposalBlock.busy"
              :previewing="proposalBlock.previewing"
              :committed="proposalBlock.committed"
              :committed-rule-id="proposalBlock.committedRuleId"
              :dismissed="proposalBlock.dismissed"
              :dismissing="proposalBlock.dismissing"
              :error="proposalBlock.error"
              :bundle-controls-active="actionablePendingCount(item) >= 2"
              @confirm="$emit('confirm-proposal', proposalBlock.proposal.proposalId)"
              @preview="$emit('preview-proposal', proposalBlock.proposal.proposalId)"
              @dismiss="$emit('dismiss-proposal', proposalBlock.proposal.proposalId)"
            />
          </section>
        </template>
      </template>

      <div
        v-if="role === 'assistant' && pending && !hasVisibleContent"
        class="chat-message__typing"
        aria-label="Assistant is typing"
      >
        <span class="dot" />
        <span class="dot" />
        <span class="dot" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import MessagePermissionDenied from './MessagePermissionDenied.vue';
import MessagePreviewStatus from './MessagePreviewStatus.vue';
import MessageProposalCard from './MessageProposalCard.vue';
import { renderMarkdown } from '../formatting';
import type { ChatBlock, ProposalBlock, ThinkingBlock } from '../types';

const props = defineProps<{
  role: 'user' | 'assistant';
  blocks?: ChatBlock[];
  pending?: boolean;
}>();

const emit = defineEmits<{
  (e: 'confirm-proposal', proposalId: string): void;
  (e: 'preview-proposal', proposalId: string): void;
  (e: 'preview-bundle', proposalIds: string[]): void;
  (e: 'dismiss-proposal', proposalId: string): void;
}>();

interface BlockItem { kind: 'block'; key: string; block: ChatBlock }
interface BundleItem { kind: 'bundle'; key: string; bundleId: string; proposals: ProposalBlock[] }
type RenderItem = BlockItem | BundleItem;

// Coalesce adjacent proposal blocks that share a non-null bundleId into a single bundle
// container. Backend guarantees they arrive in order in the same assistant message, so a
// linear scan over the block list is enough — we don't reorder.
const renderItems = computed<RenderItem[]>(() => {
  const blocks = props.blocks ?? [];
  const out: RenderItem[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.kind === 'proposal' && block.proposal.bundleId) {
      const bundleId = block.proposal.bundleId;
      const group: ProposalBlock[] = [block];
      let j = i + 1;
      while (j < blocks.length) {
        const next = blocks[j];
        if (next.kind === 'proposal' && next.proposal.bundleId === bundleId) {
          group.push(next);
          j += 1;
        } else {
          break;
        }
      }
      out.push({ kind: 'bundle', key: `bundle-${bundleId}-${i}`, bundleId, proposals: group });
      i = j;
    } else {
      out.push({ kind: 'block', key: block.id ?? `block-${i}`, block });
      i += 1;
    }
  }
  return out;
});

const hasVisibleContent = computed(() =>
  (props.blocks ?? []).some((b) => b.kind !== 'text' || (b.text ?? '').length > 0)
);

// A proposal is "actionable pending" when it can still be confirmed or dismissed. Cards
// that have already committed, been dismissed, or returned an unrecoverable error from the
// elevated-permission check are out of the bundle's actionable set.
function isActionablePending(block: ProposalBlock): boolean {
  return !block.committed
    && !block.dismissed
    && !block.proposal.requiresElevatedPermission;
}

function actionablePendingCount(bundle: BundleItem): number {
  return bundle.proposals.reduce(
    (count, b) => count + (isActionablePending(b) ? 1 : 0),
    0
  );
}

function confirmBundle(bundle: BundleItem) {
  for (const block of bundle.proposals) {
    if (isActionablePending(block) && !block.busy && !block.dismissing) {
      emit('confirm-proposal', block.proposal.proposalId);
    }
  }
}

function dismissBundle(bundle: BundleItem) {
  for (const block of bundle.proposals) {
    if (isActionablePending(block) && !block.busy && !block.dismissing) {
      emit('dismiss-proposal', block.proposal.proposalId);
    }
  }
}

// Only the latest thinking line in an in-flight assistant turn ticks. Earlier ones are
// historical context — they represent steps the bot has already moved past.
const latestThinkingId = computed<string | null>(() => {
  if (!props.pending) return null;
  const blocks = props.blocks ?? [];
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    if (blocks[i].kind === 'thinking') return blocks[i].id;
  }
  return null;
});

function isLiveThinking(block: ThinkingBlock): boolean {
  return block.id === latestThinkingId.value;
}

// 1s ticker, only running while a live thinking block is visible. Without the gate the
// component would re-render every second forever on any rendered chat history.
const now = ref(Date.now());
let tickHandle: number | null = null;
watch(latestThinkingId, (id) => {
  if (id !== null && tickHandle === null) {
    now.value = Date.now();
    tickHandle = window.setInterval(() => { now.value = Date.now(); }, 1000);
  } else if (id === null && tickHandle !== null) {
    window.clearInterval(tickHandle);
    tickHandle = null;
  }
}, { immediate: true });
onUnmounted(() => {
  if (tickHandle !== null) window.clearInterval(tickHandle);
});

function elapsedSeconds(block: ThinkingBlock): number {
  return Math.max(0, Math.floor((now.value - block.startedAt) / 1000));
}

function formatElapsed(block: ThinkingBlock): string {
  const s = elapsedSeconds(block);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return rs === 0 ? `${m}m` : `${m}m ${rs}s`;
}

function stageHint(block: ThinkingBlock): string {
  const s = elapsedSeconds(block);
  // Reality is up to a minute; the bot's own text says "a few seconds", so once we cross
  // that threshold we reset expectations rather than letting the line look stuck.
  if (s >= 90) return ' · this is taking longer than usual';
  if (s >= 30) return ' · still working';
  return '';
}

function previewBundle(bundle: BundleItem) {
  // Build the id list from currently actionable siblings only — committed/dismissed cards
  // would be rejected by upstream anyway, and we don't want a spurious "invalidProposalIds"
  // warning for a card the user already decided on.
  const ids = bundle.proposals
    .filter((b) => isActionablePending(b) && !b.previewing && !b.busy && !b.dismissing)
    .map((b) => b.proposal.proposalId);
  if (ids.length === 0) return;
  emit('preview-bundle', ids);
}

function bundlePreviewBusy(bundle: BundleItem): boolean {
  // Disable "Preview all" while any actionable sibling is mid-preview, so a second click
  // can't queue an overlapping bundle run on top of an in-flight one.
  return bundle.proposals.some((b) => isActionablePending(b) && b.previewing);
}
</script>

<style scoped lang="scss">
.chat-message {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 12px;
  width: 100%;

  &--user {
    flex-direction: row-reverse;
  }

  &__avatar {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #3b6cf3;
    margin: 0 4px 8px;
  }

  &__bubble {
    max-width: min(85%, 560px);
    min-width: 0;
    padding: 10px 14px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.55;
    color: #111827;
    word-wrap: break-word;
    overflow-wrap: anywhere;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--user {
      background: #eef2ff;
      color: #1e3a8a;
      border-bottom-right-radius: 4px;
    }

    &--assistant {
      background: #ffffff;
      border: 1px solid #eceff3;
      border-bottom-left-radius: 4px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }
  }

  // Proposal cards establish their own inline-size query container, which also
  // gives them inline-size containment: they contribute nothing to the intrinsic
  // width of their ancestors. A bubble is a shrink-to-fit flex item, so one
  // holding proposal cards would otherwise collapse to the width of whatever
  // plain text sits beside them — the thinking line, or just the padding when
  // there is no text at all. Grow those bubbles to the space available instead;
  // max-width above still caps them.
  &__bubble:has(.proposal-card) {
    flex-grow: 1;
  }

  &__text {
    :deep(p) {
      margin: 0;
    }

    :deep(p + p),
    :deep(p + ul),
    :deep(p + ol),
    :deep(ul + p),
    :deep(ol + p),
    :deep(p + pre),
    :deep(pre + p) {
      margin-top: 6px;
    }

    :deep(ul),
    :deep(ol) {
      margin: 0;
      padding-left: 20px;
    }

    :deep(li + li) {
      margin-top: 2px;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4) {
      margin: 6px 0 2px;
      font-size: 1em;
      font-weight: 600;
      line-height: 1.3;
    }

    :deep(strong) { font-weight: 600; }
    :deep(em) { font-style: italic; }

    :deep(a) {
      color: #1d4ed8;
      text-decoration: underline;
    }

    :deep(code) {
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
      font-size: 0.92em;
      padding: 1px 5px;
      background: #f1f5f9;
      color: #1e293b;
      border-radius: 4px;
      border: 1px solid #e2e8f0;
      word-break: break-word;
    }

    :deep(pre) {
      margin: 0;
      padding: 8px 10px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow-x: auto;

      code {
        padding: 0;
        background: transparent;
        border: 0;
        border-radius: 0;
      }
    }
  }

  &__bundle {
    margin: 6px 0;
    padding: 4px 8px 8px;
    border: 1px solid #c7d2fe;
    border-left-width: 3px;
    border-left-color: #93b4f5;
    background: linear-gradient(180deg, #f5f8ff 0%, #ffffff 24px);
    border-radius: 10px;

    // Inside the bundle frame, cards drop their own border so the bundle reads as the
    // primary container and the cards as items nested inside it.
    :deep(.proposal-card) {
      border-color: #e0e7ff;
      box-shadow: none;
    }
  }

  &__bundle-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 6px 6px;
  }

  &__bundle-title {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #1d4ed8;
  }

  &__bundle-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__bundle-btn {
    height: 26px;
    padding: 0 10px;
    border-radius: 6px;
    border: 1px solid #c7d2fe;
    background: #ffffff;
    color: #1e40af;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 120ms ease, border-color 120ms ease;

    &:hover:not(:disabled) { background: #eef2ff; border-color: #93b4f5; }
    &:disabled { color: #9ca3af; border-color: #e5e7eb; cursor: not-allowed; }

    &--primary {
      background: #3b6cf3;
      border-color: #3b6cf3;
      color: #ffffff;

      &:hover:not(:disabled) { background: #2952d8; border-color: #2952d8; }
    }
  }

  &__thinking {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
    font-size: 12.5px;
    font-style: italic;
    color: #6b7280;
    line-height: 1.4;
  }

  &__thinking--live &__thinking-icon {
    animation: ra-thinking-pulse 1.4s ease-in-out infinite;
  }

  &__thinking-meta {
    font-style: normal;
    font-variant-numeric: tabular-nums;
    color: #9ca3af;
  }

  @keyframes ra-thinking-pulse {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 1; }
  }

  &__typing {
    display: inline-flex;
    gap: 4px;
    padding: 4px 2px;

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #9ca3af;
      animation: chat-message-bounce 1.2s infinite ease-in-out;

      &:nth-child(2) { animation-delay: 0.15s; }
      &:nth-child(3) { animation-delay: 0.3s; }
    }
  }
}

@keyframes chat-message-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
  30% { transform: translateY(-3px); opacity: 1; }
}

// Adapt to the chat container's own width.
@container ra (max-width: 479px) {
  .chat-message { gap: 6px; margin-bottom: 10px; }
  .chat-message__bubble { max-width: 100%; padding: 9px 12px; font-size: 13.5px; }
  .chat-message__avatar { margin: 0 2px 6px; }
  .chat-message__bundle-head { flex-wrap: wrap; row-gap: 6px; }
  .chat-message__bundle-actions { width: 100%; justify-content: flex-end; }
  .chat-message__bundle-btn { flex: 1 1 auto; min-width: 0; padding: 0 8px; }
}

@container ra (max-width: 359px) {
  .chat-message { gap: 4px; margin-bottom: 8px; }
  .chat-message__bubble { padding: 8px 10px; font-size: 13px; border-radius: 12px; }
  .chat-message__avatar { display: none; }
  .chat-message__bundle { padding: 2px 4px 4px; }
  .chat-message__bundle-head { padding: 2px 4px 4px; }
  .chat-message__bundle-title { font-size: 11px; letter-spacing: 0.02em; }
  .chat-message__bundle-btn { height: 28px; font-size: 12px; padding: 0 6px; }
}
</style>
